import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CalendarRange,
  CheckCircle,
  FileDown,
  HardHat,
  Lock,
  RefreshCw,
  Search,
  ShieldCheck,
  Tractor,
  Users,
} from "lucide-react";
import type { PayPeriodReview, PayPeriodReviewWorker } from "@crewledger/shared/schemas";
import {
  AuditIntegrityPanel,
  ConfirmationModal,
  EmptyState,
  InfoBanner,
  ListTableSkeleton,
  PaginationBar,
  useToast,
} from "@/components/design";
import { OnchainProofPanel } from "@/features/onchain/onchain-proof-panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { cn } from "@/lib/utils";
import { MICROCOPY } from "@/lib/microcopy";
import {
  approvePayPeriod,
  approveSiteInPeriod,
  approveWorkerInPeriod,
  exportPayPeriod,
  fetchPayPeriodReview,
  lockPayPeriod,
  recalculatePayPeriod,
  requestWorkerCorrection,
  resolvePayPeriodException,
} from "./api";
import { PayPeriodStatusBadge } from "./components/pay-period-status-badge";
import { WorkerReviewStatusBadge } from "./components/worker-review-status-badge";
import { usePayPeriodPermissions } from "./use-pay-period-permissions";

type ReviewTab = "workers" | "sites" | "crews" | "equipment" | "exceptions" | "audit";

const WORKER_PAGE_SIZE = 50;

const TABS: { id: ReviewTab; label: string; icon: typeof Users }[] = [
  { id: "workers", label: "All Workers", icon: Users },
  { id: "sites", label: "By Site", icon: Building2 },
  { id: "crews", label: "By Crew", icon: HardHat },
  { id: "equipment", label: "Equipment", icon: Tractor },
  { id: "exceptions", label: "Exceptions", icon: AlertTriangle },
  { id: "audit", label: "Audit", icon: ShieldCheck },
];

function ProgressRing({ percent }: { percent: number }) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="relative flex h-16 w-16 items-center justify-center">
      <svg className="-rotate-90" width="64" height="64" aria-hidden>
        <circle cx="32" cy="32" r={radius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="5" />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          stroke="#FF8A00"
          strokeWidth="5"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-sm font-bold text-white">{percent}%</span>
    </div>
  );
}

export function PayPeriodReviewPage() {
  const { payPeriodId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { canViewPayroll } = usePayPeriodPermissions();
  const [review, setReview] = useState<PayPeriodReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<ReviewTab>("workers");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [correctionWorker, setCorrectionWorker] = useState<PayPeriodReviewWorker | null>(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [confirmAction, setConfirmAction] = useState<"approve" | "lock" | null>(null);
  const [workerPage, setWorkerPage] = useState(1);
  const [workerSearch, setWorkerSearch] = useState("");
  const [workersTotal, setWorkersTotal] = useState(0);
  const debouncedWorkerSearch = useDebouncedValue(workerSearch, 300);

  const load = useCallback(() => {
    if (!payPeriodId) return;
    setLoading(true);
    fetchPayPeriodReview(payPeriodId, {
      page: workerPage,
      pageSize: WORKER_PAGE_SIZE,
      q: debouncedWorkerSearch || undefined,
    })
      .then((res) => {
        setReview(res.data);
        setWorkersTotal(res.meta.total ?? res.data.workersTotal ?? res.data.workers.length);
      })
      .catch((err: unknown) => { setError(err instanceof Error ? err.message : "Failed to load"); })
      .finally(() => { setLoading(false); });
  }, [payPeriodId, workerPage, debouncedWorkerSearch]);

  useEffect(() => {
    setWorkerPage(1);
  }, [debouncedWorkerSearch]);

  useEffect(load, [load]);

  const runAction = async (key: string, fn: () => Promise<void>, success: string) => {
    setActionLoading(key);
    try {
      await fn();
      toast({ variant: "success", title: success });
      load();
    } catch (err) {
      toast({
        variant: "error",
        title: "Action failed",
        description: err instanceof Error ? err.message : "Something went wrong",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleWorkerApprove = async (workerId: string) => {
    if (!payPeriodId) return;
    setActionLoading(`worker-${workerId}`);
    try {
      const res = await approveWorkerInPeriod(payPeriodId, workerId);
      setReview(res.data);
      toast({ variant: "success", title: "Worker approved" });
    } catch (err) {
      toast({
        variant: "error",
        title: "Approval failed",
        description: err instanceof Error ? err.message : "Something went wrong",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSiteApprove = async (siteId: string) => {
    if (!payPeriodId) return;
    setActionLoading(`site-${siteId}`);
    try {
      const res = await approveSiteInPeriod(payPeriodId, siteId);
      setReview(res.data);
      toast({ variant: "success", title: "Site approved" });
    } catch (err) {
      toast({
        variant: "error",
        title: "Approval failed",
        description: err instanceof Error ? err.message : "Something went wrong",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRequestCorrection = async () => {
    if (!payPeriodId || !correctionWorker || !correctionReason.trim()) return;
    setActionLoading("correction");
    try {
      const res = await requestWorkerCorrection(payPeriodId, correctionWorker.id, {
        reason: correctionReason.trim(),
      });
      setReview(res.data);
      setCorrectionWorker(null);
      setCorrectionReason("");
      toast({ variant: "success", title: "Correction requested" });
    } catch (err) {
      toast({
        variant: "error",
        title: "Request failed",
        description: err instanceof Error ? err.message : "Something went wrong",
      });
    } finally {
      setActionLoading(null);
    }
  };

  if (error && !review) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="Review not available"
        description={error}
        actionLabel="Back to period"
        onAction={() => navigate(`/pay-periods/${payPeriodId}`)}
      />
    );
  }

  if (loading || !review) {
    return (
      <div className="space-y-4">
        <div className="h-40 animate-pulse rounded-xl bg-navy-deep/20" />
        <ListTableSkeleton rows={10} cols={8} />
      </div>
    );
  }

  const { totals, approvalProgress, integrity } = review;

  const integrityItems = [
    {
      label: "All entries have source",
      value: integrity.allEntriesHaveSource ? "Verified" : "Missing",
      verified: integrity.allEntriesHaveSource,
    },
    {
      label: "No rule conflicts",
      value: integrity.noRuleConflicts ? "Clear" : "Conflicts",
      verified: integrity.noRuleConflicts,
    },
    {
      label: "Exceptions reviewed",
      value: integrity.exceptionsReviewed ? "Complete" : `${totals.openExceptions} open`,
      verified: integrity.exceptionsReviewed,
    },
    {
      label: "Approvals pending",
      value: String(integrity.approvalsPending),
      verified: integrity.approvalsPending === 0,
    },
    {
      label: "On-chain proof",
      value: integrity.onchainProofStatus ?? "None",
      verified: integrity.onchainProofStatus === "PUBLISHED",
    },
  ];

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Command center header */}
      <div className="overflow-hidden rounded-xl border border-navy-deep/10 bg-navy-deep text-white shadow-[var(--shadow-elevated)]">
        <div className="blueprint-bg-subtle relative px-5 py-5 sm:px-6">
          <div className="absolute inset-0 bg-navy-deep/95" aria-hidden />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-orange-construction text-navy-deep">
                <CalendarRange className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-orange-construction">
                  Payroll Review
                </p>
                <h1 className="text-2xl font-bold">{review.label}</h1>
                <p className="mt-1 text-sm text-white/70">
                  {review.startDate} → {review.endDate}
                </p>
                <div className="mt-2">
                  <PayPeriodStatusBadge status={review.status} />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <ProgressRing percent={approvalProgress.percentComplete} />
              <div className="text-sm">
                <p className="font-semibold text-white">Approval progress</p>
                <p className="text-white/70">
                  {approvalProgress.workersApproved}/{approvalProgress.workersTotal} workers
                </p>
                <p className="text-white/70">
                  {approvalProgress.sitesApproved}/{approvalProgress.sitesTotal} sites
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 gap-px bg-navy-blueprint/60 sm:grid-cols-4 lg:grid-cols-9">
          {[
            { label: "Workers", value: totals.totalWorkers },
            { label: "Regular", value: formatHours(totals.regularHours) },
            { label: "OT", value: formatHours(totals.overtimeHours) },
            { label: "DT", value: formatHours(totals.doubleTimeHours) },
            ...(canViewPayroll
              ? [
                  { label: "Gross pay", value: formatCurrency(totals.grossPay) },
                  { label: "Equipment", value: formatCurrency(totals.equipmentCost) },
                ]
              : []),
            {
              label: "Exceptions",
              value: totals.openExceptions,
              alert: totals.openExceptions > 0,
            },
            {
              label: "Critical",
              value: totals.criticalExceptions,
              alert: totals.criticalExceptions > 0,
            },
            { label: "Entries", value: `${approvalProgress.entriesApproved}/${approvalProgress.entriesTotal}` },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-navy-blueprint px-3 py-3 sm:px-4">
              <p className="text-[10px] uppercase tracking-wider text-white/55">{kpi.label}</p>
              <p
                className={cn(
                  "mt-0.5 text-base font-bold tabular-nums sm:text-lg",
                  "alert" in kpi && kpi.alert ? "text-amber-300" : "text-white",
                )}
              >
                {kpi.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {(review.status === "LOCKED" || review.status === "EXPORTED") ? (
        <InfoBanner icon={Lock} variant="warning" title="Period locked">
          {MICROCOPY.lockedPayPeriod} {MICROCOPY.auditLogSaved}
        </InfoBanner>
      ) : (
        <InfoBanner icon={ShieldCheck} variant="info">
          Review worker totals, resolve exceptions, then approve and lock before exporting.{" "}
          {MICROCOPY.equipmentCostSeparate}
        </InfoBanner>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-card-white px-4 py-3">
        <span className="mr-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Actions
        </span>
        {review.canApprove ? (
          <Button
            size="sm"
            disabled={actionLoading === "approve-period"}
            onClick={() => { setConfirmAction("approve"); }}
          >
            <CheckCircle className="mr-1.5 h-4 w-4" />
            Approve period
          </Button>
        ) : null}
        {review.canLock ? (
          <Button
            size="sm"
            disabled={actionLoading === "lock"}
            onClick={() => { setConfirmAction("lock"); }}
          >
            <Lock className="mr-1.5 h-4 w-4" />
            Lock period
          </Button>
        ) : null}
        {review.canExport ? (
          <Button
            size="sm"
            variant="outline"
            disabled={actionLoading === "export"}
            onClick={() =>
              runAction(
                "export",
                async () => {
                  if (!payPeriodId) return;
                  await exportPayPeriod(payPeriodId, { type: "PAYROLL" });
                },
                "Export queued",
              )
            }
          >
            <FileDown className="mr-1.5 h-4 w-4" />
            Export
          </Button>
        ) : null}
        {review.canRecalculate ? (
          <Button
            size="sm"
            variant="outline"
            disabled={actionLoading === "recalculate"}
            onClick={() =>
              runAction(
                "recalculate",
                async () => {
                  if (!payPeriodId) return;
                  await recalculatePayPeriod(payPeriodId, { async: true });
                },
                "Recalculation queued",
              )
            }
          >
            <RefreshCw className="mr-1.5 h-4 w-4" />
            Recalculate
          </Button>
        ) : null}
        <Link to={`/pay-periods/${payPeriodId}`} className="ml-auto">
          <Button size="sm" variant="ghost">
            Period details
          </Button>
        </Link>
      </div>

      {/* Main layout: tabs + integrity panel */}
      <div className="grid gap-5 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_300px]">
        <div className="min-w-0 space-y-4">
          {/* Tab bar */}
          <div className="flex gap-1 overflow-x-auto rounded-lg border bg-bg-light p-1">
            {TABS.map(({ id, label, icon: Icon }) => {
              const count =
                id === "workers"
                  ? workersTotal || review.workersTotal || review.workers.length
                  : id === "sites"
                    ? review.sites.length
                    : id === "crews"
                      ? review.crews.length
                      : id === "equipment"
                        ? review.equipment.length
                        : id === "exceptions"
                          ? review.exceptions.length
                          : review.auditActivity.length;

              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => { setTab(id); }}
                  className={cn(
                    "flex shrink-0 items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    tab === id
                      ? "bg-card-white text-navy-deep shadow-sm"
                      : "text-muted-foreground hover:text-navy-deep",
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                  <span className="rounded-full bg-navy-deep/8 px-1.5 text-[10px] tabular-nums">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="overflow-hidden rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
            {tab === "workers" ? (
              <>
                <div className="border-b px-4 py-3">
                  <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search employee ID or name…"
                      value={workerSearch}
                      onChange={(e) => { setWorkerSearch(e.target.value); }}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-sm">
                  <thead className="border-b bg-navy-deep/3 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Employee</th>
                      <th className="px-3 py-3 font-semibold">Site</th>
                      <th className="px-3 py-3 font-semibold text-right">Reg</th>
                      <th className="px-3 py-3 font-semibold text-right">OT</th>
                      <th className="px-3 py-3 font-semibold text-right">DT</th>
                      <th className="px-3 py-3 font-semibold text-right">Holiday</th>
                      {canViewPayroll ? (
                        <>
                          <th className="px-3 py-3 font-semibold text-right">Premium</th>
                          <th className="px-3 py-3 font-semibold text-right">Gross</th>
                          <th className="px-3 py-3 font-semibold text-right">Equip.</th>
                        </>
                      ) : null}
                      <th className="px-3 py-3 font-semibold">Status</th>
                      <th className="px-3 py-3 font-semibold text-right">Exc.</th>
                      <th className="px-4 py-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {review.workers.map((worker) => (
                      <tr
                        key={worker.id}
                        className={cn(
                          "border-b border-border/40 transition-colors hover:bg-bg-light/60",
                          worker.status === "EXCEPTION" && "bg-amber-50/40",
                          worker.status === "NEEDS_CORRECTION" && "bg-red-50/30",
                        )}
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium text-navy-deep">{worker.name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">
                            {worker.employeeId}
                          </p>
                        </td>
                        <td className="px-3 py-3">
                          <span className="font-mono text-xs text-navy-blueprint">{worker.siteCode}</span>
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(worker.regularHours)}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(worker.overtimeHours)}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(worker.doubleTimeHours)}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(worker.holidayHours)}
                        </td>
                        {canViewPayroll ? (
                          <>
                            <td className="px-3 py-3 text-right font-mono text-xs">
                              {formatCurrency(worker.premiumPay)}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-xs font-semibold">
                              {formatCurrency(worker.grossPay)}
                            </td>
                            <td className="px-3 py-3 text-right font-mono text-xs">
                              {formatCurrency(worker.equipmentCost)}
                            </td>
                          </>
                        ) : null}
                        <td className="px-3 py-3">
                          <WorkerReviewStatusBadge status={worker.status} />
                        </td>
                        <td className="px-3 py-3 text-right">
                          {worker.openExceptions > 0 ? (
                            <span className="font-semibold text-amber-warning">
                              {worker.openExceptions}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {worker.canApprove ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                disabled={actionLoading === `worker-${worker.id}`}
                                onClick={() => handleWorkerApprove(worker.id)}
                              >
                                Approve
                              </Button>
                            ) : null}
                            {worker.canRequestCorrection ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs text-red-danger"
                                onClick={() => { setCorrectionWorker(worker); }}
                              >
                                Correct
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                <PaginationBar
                  page={workerPage}
                  pageSize={WORKER_PAGE_SIZE}
                  total={workersTotal}
                  onPageChange={setWorkerPage}
                />
              </>
            ) : null}

            {tab === "sites" ? (
              <div className="divide-y">
                {review.sites.map((site) => (
                  <div
                    key={site.id}
                    className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-semibold text-orange-construction">
                          {site.code}
                        </span>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase",
                            site.status === "APPROVED"
                              ? "bg-green-50 text-green-700"
                              : site.status === "EXCEPTION"
                                ? "bg-amber-50 text-amber-800"
                                : "bg-blue-50 text-blue-700",
                          )}
                        >
                          {site.status}
                        </span>
                      </div>
                      <p className="font-semibold text-navy-deep">{site.name}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {site.workerCount} workers · {formatHours(site.regularHours)} reg ·{" "}
                        {formatHours(site.overtimeHours)} OT
                        {canViewPayroll ? ` · ${formatCurrency(site.grossPay)} gross` : ""}
                      </p>
                    </div>
                    {site.canApprove ? (
                      <Button
                        size="sm"
                        disabled={actionLoading === `site-${site.id}`}
                        onClick={() => handleSiteApprove(site.id)}
                      >
                        Approve site
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {tab === "crews" ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b bg-navy-deep/3 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Crew</th>
                      <th className="px-3 py-3 font-semibold text-right">Workers</th>
                      <th className="px-3 py-3 font-semibold text-right">Reg</th>
                      <th className="px-3 py-3 font-semibold text-right">OT</th>
                      <th className="px-3 py-3 font-semibold text-right">DT</th>
                      {canViewPayroll ? (
                        <th className="px-3 py-3 font-semibold text-right">Gross</th>
                      ) : null}
                      <th className="px-3 py-3 font-semibold text-right">Exc.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {review.crews.map((crew) => (
                      <tr key={crew.name} className="border-b border-border/40">
                        <td className="px-4 py-3 font-medium">{crew.name}</td>
                        <td className="px-3 py-3 text-right">{crew.workerCount}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(crew.regularHours)}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(crew.overtimeHours)}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(crew.doubleTimeHours)}
                        </td>
                        {canViewPayroll ? (
                          <td className="px-3 py-3 text-right font-mono text-xs">
                            {formatCurrency(crew.grossPay)}
                          </td>
                        ) : null}
                        <td className="px-3 py-3 text-right">
                          {crew.openExceptions > 0 ? (
                            <span className="text-amber-warning">{crew.openExceptions}</span>
                          ) : (
                            "0"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {tab === "equipment" ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b bg-navy-deep/3 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Equipment</th>
                      <th className="px-3 py-3 font-semibold">ID</th>
                      <th className="px-3 py-3 font-semibold text-right">Usage hrs</th>
                      <th className="px-3 py-3 font-semibold text-right">Workers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {review.equipment.map((item) => (
                      <tr key={item.id} className="border-b border-border/40">
                        <td className="px-4 py-3 font-medium">{item.name}</td>
                        <td className="px-3 py-3 font-mono text-xs">{item.code ?? "—"}</td>
                        <td className="px-3 py-3 text-right font-mono text-xs">
                          {formatHours(item.usageHours)}
                        </td>
                        <td className="px-3 py-3 text-right">{item.workerCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {tab === "exceptions" ? (
              review.exceptions.length === 0 ? (
                <EmptyState
                  icon={CheckCircle}
                  title="No exceptions"
                  description="All entries passed validation. You're clear to approve this pay period."
                  className="m-4 border-0"
                />
              ) : (
              <ul className="divide-y">
                {review.exceptions.map((ex) => (
                    <li key={ex.id} className="flex items-start gap-3 px-5 py-4">
                      <AlertTriangle
                        className={cn(
                          "mt-0.5 h-4 w-4 shrink-0",
                          ex.severity === "CRITICAL" ? "text-red-danger" : "text-amber-warning",
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-navy-deep">{ex.message}</p>
                        <p className="text-xs text-muted-foreground">
                          {ex.type} · {ex.status} · {ex.severity}
                        </p>
                      </div>
                      {ex.status === "OPEN" && review.canApprove ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            void resolvePayPeriodException(ex.id, "Reviewed in pay period workflow")
                              .then(() => {
                                toast({
                                  variant: "success",
                                  title: "Exception resolved",
                                });
                                load();
                              })
                              .catch((err: unknown) =>
                                { toast({
                                  variant: "error",
                                  title: "Could not resolve exception",
                                  description: err instanceof Error ? err.message : undefined,
                                }); },
                              )
                          }
                        >
                          Resolve
                        </Button>
                      ) : null}
                    </li>
                  ))}
              </ul>
              )
            ) : null}

            {tab === "audit" ? (
              review.auditActivity.length === 0 ? (
                <EmptyState
                  icon={ShieldCheck}
                  title="No activity yet"
                  description={MICROCOPY.auditLogSaved}
                  className="m-4 border-0"
                />
              ) : (
              <ul className="divide-y">
                  {review.auditActivity.map((entry) => (
                    <li key={entry.id} className="px-5 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium capitalize text-navy-deep">
                            {entry.action.replace(/\./g, " ")}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {entry.actorName ?? "System"}
                            {entry.reason ? ` · ${entry.reason}` : null}
                          </p>
                        </div>
                        <time className="shrink-0 text-xs text-muted-foreground">
                          {new Date(entry.createdAt).toLocaleString()}
                        </time>
                      </div>
                    </li>
                  ))}
              </ul>
              )
            ) : null}
          </div>
        </div>

        {/* Side panel */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <AuditIntegrityPanel
            title="Audit & Integrity"
            items={integrityItems}
            hashPreview={integrity.merkleRootPreview ?? undefined}
          />

          <OnchainProofPanel
            payPeriodId={payPeriodId!}
            integrity={integrity}
            onUpdated={() => { load(); }}
            onError={(message) => { toast({ variant: "error", title: "Onchain proof", description: message }); }}
            onSuccess={(message) => { toast({ variant: "success", title: "Onchain proof", description: message }); }}
          />

          {totals.criticalExceptions > 0 ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              <p className="font-semibold">{totals.criticalExceptions} critical exceptions</p>
              <p className="mt-1 text-xs">Must be resolved before lock or export.</p>
            </div>
          ) : null}

          <div className="rounded-lg border bg-card-white p-4 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Quick stats
            </p>
            <dl className="mt-3 space-y-2">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Pending approvals</dt>
                <dd className="font-semibold tabular-nums">{integrity.approvalsPending}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Workers complete</dt>
                <dd className="font-semibold tabular-nums">
                  {approvalProgress.workersApproved}/{approvalProgress.workersTotal}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Sites complete</dt>
                <dd className="font-semibold tabular-nums">
                  {approvalProgress.sitesApproved}/{approvalProgress.sitesTotal}
                </dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>

      {/* Correction modal */}
      {correctionWorker ? (
        <dialog
          open
          className="fixed inset-0 z-50 m-auto w-full max-w-md rounded-xl border bg-card-white p-6 shadow-lg backdrop:bg-navy-deep/50"
        >
          <h2 className="text-lg font-semibold text-navy-deep">Request correction</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Send {correctionWorker.name}&apos;s entries back for correction.
          </p>
          <div className="mt-4 space-y-2">
            <Label htmlFor="correctionReason">Reason</Label>
            <Input
              id="correctionReason"
              value={correctionReason}
              onChange={(e) => { setCorrectionReason(e.target.value); }}
              placeholder="Describe what needs to be corrected…"
            />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCorrectionWorker(null);
                setCorrectionReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={actionLoading === "correction" || !correctionReason.trim()}
              onClick={handleRequestCorrection}
            >
              {actionLoading === "correction" ? "Sending…" : "Send back"}
            </Button>
          </div>
        </dialog>
      ) : null}

      <ConfirmationModal
        open={confirmAction === "approve"}
        onClose={() => { setConfirmAction(null); }}
        onConfirm={() => {
          setConfirmAction(null);
          void runAction(
            "approve-period",
            async () => {
              if (!payPeriodId) return;
              await approvePayPeriod(payPeriodId);
            },
            "Pay period approved",
          );
        }}
        title="Approve pay period?"
        description={`Confirm totals for ${review.label}. ${MICROCOPY.auditLogSaved}`}
        confirmLabel="Approve period"
        loading={actionLoading === "approve-period"}
      />

      <ConfirmationModal
        open={confirmAction === "lock"}
        onClose={() => { setConfirmAction(null); }}
        onConfirm={() => {
          setConfirmAction(null);
          void runAction(
            "lock",
            async () => {
              if (!payPeriodId) return;
              await lockPayPeriod(payPeriodId);
            },
            "Pay period locked",
          );
        }}
        title="Lock pay period?"
        description={MICROCOPY.lockedPayPeriod}
        confirmLabel="Lock period"
        variant="destructive"
        loading={actionLoading === "lock"}
      />
    </div>
  );
}
